<template>
  <m-pageframe title="Public Shortened Link Domains">
    <v-container fluid style="padding: 15px">
    <div class="mb-5">
      <h1 class="text-h5 font-weight-medium">Shortened Link Domains</h1>
      <p class="text-subtitle-1 font-weight-regular text--secondary mt-2">
        This page controls the list of domains RedChirp uses to identify shortened URLs in bulk messages and automated messages. To stay current, the core list is automatically synced daily from a public GitHub repository. You can supplement this by adding custom domains or archiving any entry to remove it from detection. The system checks for links from all <strong>active</strong> domains shown below, as well as RedChirp's branded short links.
      </p>
    </div>

      <v-card>
        <v-card-title>
          <v-spacer />
          <v-text-field
            v-model="full_text"
            prepend-icon="mdi-magnify"
            label="Search domains..."
            single-line
            hide-details
            clearable
            dense
            style="max-width: 360px"
            @input="onSearchInput"
          />
          <v-btn color="primary" class="ml-2" @click="openAddDialog">Add Domain...</v-btn>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="50"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
          :loading="loading"
          loading-text="Loading domains..."
          no-data-text="No shortened link domains found"
          class="enhanced-data-table enhanced-data-table--domains elevation-1"
        >
          <template v-slot:[`item.source`]="{ item }">
            <v-chip small :color="getSourceColor(item.source)" text-color="white">
              {{ item.source }}
            </v-chip>
          </template>

          <template v-slot:[`item.is_active`]="{ item }">
            <v-chip small :color="item.is_active ? 'green' : 'red'" text-color="white">
              {{ item.is_active ? 'Active' : 'Archived' }}
            </v-chip>
          </template>

          <template v-slot:[`item.time_created`]="{ item }">
            <span>{{ formatTs(item.time_created) }}</span>
          </template>

          <template v-slot:[`item.time_last_seen`]="{ item }">
            <span>{{ formatTs(item.time_last_seen) }}</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn small text color="orange" @click="openArchiveDialog(item)">{{ item.is_active ? 'Archive' : 'Unarchive' }}</v-btn>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="add_dialog" max-width="480px">
        <v-card>
          <v-card-title class="headline">Add Shortened Link Domain</v-card-title>
          <v-card-text>
            <p class="mb-3 text-body-2">
              Add a new domain to the shortened link detection list. Once added, RedChirp will automatically
              identify URLs from this domain as shortened links.
            </p>
            <v-text-field v-model="new_domain" label="Domain (e.g., bit.ly)" autofocus clearable />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="add_dialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="onCreate">Add Domain</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="archive_dialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">{{ archive_target && archive_target.is_active ? 'Archive Domain' : 'Unarchive Domain' }}</v-card-title>
          <v-card-text>
            <p class="mb-3 text-body-2">
              {{ archive_target && archive_target.is_active
                ? 'Archiving will temporarily disable shortened link detection for this domain. The domain will remain in the list but will not be used for URL identification until unarchived.'
                : 'Unarchiving will re-enable shortened link detection for this domain, allowing RedChirp to identify URLs from this domain again.' }}
            </p>
            <div>
              {{ archive_target && archive_target.is_active ? 'Are you sure you want to archive' : 'Are you sure you want to unarchive' }}
              <b>{{ archive_target && archive_target.domain }}</b>?
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="archive_dialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="confirmArchive">{{ archive_target && archive_target.is_active ? 'Archive' : 'Unarchive' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </m-pageframe>
</template>

<script>
export default {
  name: 'AdminShortenedLinkDomains',
  data() {
    return {
      headers: [
        { text: 'Domain', value: 'domain' },
        { text: 'Source', value: 'source', sortable: false },
        { text: 'Status', value: 'is_active', sortable: false, align: 'center' },
        { text: 'Created', value: 'time_created' },
        { text: 'Last Seen', value: 'time_last_seen' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      items: [],
      full_text: '',
      new_domain: '',
      loading: false,
      add_dialog: false,
      archive_dialog: false,
      archive_target: null,
      debounce_timer: null,
    }
  },
  mounted() { this.update(); },
  beforeDestroy() {
    if (this.debounce_timer) { clearTimeout(this.debounce_timer); }
  },
  methods: {
    getSourceColor(source) {
      return source === 'manual' ? 'blue' : 'green';
    },
    onSearchInput() {
      if (this.debounce_timer) clearTimeout(this.debounce_timer);
      this.debounce_timer = setTimeout(() => this.update(), 751);
    },
    formatTs(ts) {
      if (!ts) return '';
      try { return new Date(ts).toLocaleString(); } catch(e) { return ts; }
    },
    update() {
      this.loading = true;
      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/admin/shortened-link-domains',
        params: { full_text: this.full_text },
        body: {},
        on_success: (data) => { this.items = data.results || []; this.loading = false; },
        on_error: () => { this.loading = false; this.$rc.snackbar('Unable to load shortened link domains', 'error'); },
      });
    },
    openAddDialog() { this.add_dialog = true; },
    
    onCreate() {
      const domain = (this.new_domain || '').trim().toLowerCase();
      if (!domain) return;
      this.$rc.rest({
        'spinner': false,
        'endpoint': 'PUT: /api/v1/admin/shortened-link-domains/create',
        'params': { domain },
        'on_success': () => { this.new_domain=''; this.add_dialog = false; this.update(); },
        'on_error': (error) => {
            this.$rc.alert('Error', error.message || 'Unable to add domain');
        },
      });
    },
    openArchiveDialog(item) {
      this.archive_target = item;
      this.archive_dialog = true;
    },
    confirmArchive() {
      if (!this.archive_target) return;
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/admin/shortened-link-domains/archive',
        params: { domain: this.archive_target.domain, is_active: !this.archive_target.is_active },
        on_success: () => { this.archive_dialog = false; this.archive_target = null; this.update(); },
        on_error: (error) => {
            this.$rc.alert('Error', error.message || 'Unable to archive domain');
        },
      });
    },
    onToggleActive(item) {
      this.openArchiveDialog(item);
    },
    
  }
}
</script>

<style scoped>
</style>
